import {
  Box,
  Drawer,
  SwipeableDrawer,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";
import React, { ReactNode, useCallback, useRef } from "react";

import { SvgIconProps } from "@mui/material/SvgIcon";
import { useResponsive } from "src/reusable/hooks/useResponsive";
import { SidebarTabId } from "./types";
import PullHandle from "./PullHandle";

interface ResponsiveSidebarProps {
  children: ReactNode;
  open: boolean;
  onClose: () => void;
  onOpen?: () => void;
  width?: number | string | Record<string, number>;
  height?: string;
  currentView: SidebarTabId | null;
  onViewChange: (view: SidebarTabId | null) => void;
  views: Array<{
    id: SidebarTabId;
    label: string;
    icon: React.ComponentType<SvgIconProps>;
  }>;
  // Desktop only: allow user drag-resize. If number provided in width, this will be adjustable.
  resizable?: boolean;
  minWidth?: number;
  maxWidth?: number;
  onResize?: (width: number) => void;
  /** Collapse (auto-close) when dragged below this width (px). Set to 0 to disable auto close */
  collapseThreshold?: number;
}

const ResponsiveSidebar = ({
  children,
  open,
  onClose,
  onOpen = () => {},
  width = 420,
  height = "70vh",
  currentView,
  onViewChange,
  views,
  resizable = false,
  minWidth = 0,
  maxWidth = Number.POSITIVE_INFINITY,
  onResize,
  collapseThreshold = 40,
}: ResponsiveSidebarProps) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef<number | null>(null);

  const numericWidth = typeof width === "number" ? width : undefined;

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      if (startWidthRef.current == null) return;
      const delta = startXRef.current - e.clientX; // dragging left increases width (right anchored)
      const viewportMax = window.innerWidth;
      const dynamicMax = Math.min(maxWidth, viewportMax);
      let newWidth = startWidthRef.current + delta;
      newWidth = Math.max(minWidth ?? 0, Math.min(dynamicMax, newWidth));
      if (collapseThreshold > 0 && newWidth <= collapseThreshold) {
        // Treat as fully collapsed (width 0) and close
        onClose();
        isDraggingRef.current = false;
        document.body.style.cursor = "";
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", endDrag);
        return;
      }
      onResize?.(newWidth);
    },
    [onResize, minWidth, maxWidth, collapseThreshold, onClose],
  );

  const endDrag = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      document.body.style.cursor = "";
    }
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", endDrag);
  }, [onMouseMove]);

  const startDrag = useCallback(
    (e: React.MouseEvent) => {
      if (!resizable || isMobile) return;
      if (numericWidth == null) return;
      isDraggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = numericWidth;
      document.body.style.cursor = "col-resize";
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", endDrag);
      e.preventDefault();
      e.stopPropagation();
    },
    [resizable, isMobile, numericWidth, onMouseMove, endDrag],
  );

  const drawerContent = (
    <Box
      sx={{
        width: isMobile ? "100%" : width,
        height: isMobile ? height : "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {!isMobile && resizable && typeof width === "number" && (
        <Box
          onMouseDown={startDrag}
          aria-label="Resize sidebar"
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "4px",
            backgroundColor: "transparent",
            cursor: "col-resize",
            flexShrink: 0,
            zIndex: 10,
            "&::after": {
              content: '""',
              position: "absolute",
              left: "1px",
              top: 0,
              bottom: 0,
              width: "2px",
              backgroundColor: theme.palette.grey[300],
              borderRadius: "1px",
              opacity: 0.7,
              transition: "all 150ms ease-out",
            },
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
            },
            "&:hover::after": {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
            },
            "&:hover::before": {
              content: '""',
              position: "absolute",
              left: "-2px",
              right: "-2px",
              top: 0,
              bottom: 0,
              backgroundColor: theme.palette.primary.main,
              opacity: 0.3,
            },
          }}
        />
      )}
      {isMobile && <PullHandle />}
      {isMobile && (
        <Tabs
          value={currentView}
          onChange={(_, newValue) => onViewChange(newValue)}
          variant="fullWidth"
        >
          {views.map((view) => (
            <Tab
              key={view.id}
              label={view.label}
              value={view.id}
              icon={<view.icon fontSize="small" />}
              iconPosition="start"
              sx={{
                textTransform: "none",
                gap: 0.5,
              }}
            />
          ))}
        </Tabs>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          mt: 0,
          "& > *": {
            minWidth: 0,
            maxWidth: "100%",
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={onOpen}
        disableSwipeToOpen={false}
        swipeAreaWidth={20}
        disableDiscovery={false}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          "& .MuiDrawer-paper": {
            borderTopLeftRadius: theme.spacing(2),
            borderTopRightRadius: theme.spacing(2),
            maxHeight: height,
            overflow: "visible",
          },
        }}
      >
        {drawerContent}
      </SwipeableDrawer>
    );
  }

  if (!open) return null;

  return (
    <Drawer
      anchor="right"
      variant="permanent"
      open
      sx={{
        "& .MuiDrawer-paper": {
          width: width,
          position: "relative",
          height: "100%",
          // Remove permanent gray divider; handle will indicate resize on hover
          borderLeft: "none",
          backgroundColor: theme.palette.background.default,
          overflowX: "hidden",
          overflowY: "auto",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default ResponsiveSidebar;
